import type { SurfaceGlowConfig } from '@Shelly/shelly-design-system';
import type { Device } from 'src/types/Device';

/** Compact HT sizes — temperature colour stays, footprint stays modest. */
export const HT_GLOW_SIZE_OVERRIDES = {
	active: {
		interiorStrength: 0.7,
		edgeStrength: 0.76,
		interiorSize: 120,
		edgeSize: 156,
	},
	inactive: {
		interiorStrength: 0.28,
		edgeStrength: 0.4,
		interiorSize: 84,
		edgeSize: 112,
	},
} as const;

/**
 * TemperatureWheel white-balance stops (warm → cool), used for CCT → glow colour.
 * Source: `TemperatureWheelGradients.svelte` `type="white-balance"`.
 */
const WHITE_BALANCE_STOPS: Array<{ t: number; rgb: [number, number, number] }> = [
	{ t: 0, rgb: [255, 169, 68] }, // #FFA944
	{ t: 0.25, rgb: [255, 194, 122] }, // #FFC27A
	{ t: 0.4, rgb: [255, 226, 191] }, // #FFE2BF
	{ t: 0.55, rgb: [254, 254, 254] }, // #FEFEFE
	{ t: 0.75, rgb: [218, 230, 255] }, // #DAE6FF
	{ t: 0.9, rgb: [164, 195, 255] }, // #A4C3FF
	{ t: 1, rgb: [155, 189, 255] }, // #9BBDFF
];

/**
 * Read temperature in °C from common Gen1 / Gen2+ status shapes.
 */
export const getDashDeviceTemperatureC = (status: any): number | null => {
	if (!status) return null;

	const gen2 = status['temperature:0']?.tC;
	if (typeof gen2 === 'number' && !Number.isNaN(gen2)) return gen2;

	const tmp = status?.tmp;
	if (typeof tmp?.value === 'number' && tmp.value !== 999 && !Number.isNaN(tmp.value)) {
		if (tmp.units === 'F') return ((tmp.value - 32) * 5) / 9;
		return tmp.value;
	}

	return null;
};

export type HtTemperatureGlow = {
	accentColor: string;
	/** Stronger glow outside the comfort band (cold / hot). */
	active: boolean;
};

/**
 * Semantic HT glow: cool / warm climate tokens. Comfort (~18–24 °C) stays idle.
 */
export const resolveHtTemperatureGlow = (temperatureC: number): HtTemperatureGlow => {
	if (temperatureC < 18) {
		return {
			accentColor: '--color-theme-climate-cool',
			active: temperatureC < 16,
		};
	}
	if (temperatureC > 24) {
		return {
			accentColor: '--color-theme-climate-warm',
			active: temperatureC > 26,
		};
	}
	return { accentColor: '--color-theme-fg-02', active: false };
};

/**
 * Map CCT kelvin onto the TemperatureWheel white-balance gradient.
 * Typical bulb range ~2700–6500 K.
 */
export const whiteBalanceKelvinToCssRgb = (kelvin: number): string => {
	const minK = 2700;
	const maxK = 6500;
	const t = Math.min(1, Math.max(0, (kelvin - minK) / (maxK - minK)));

	let left = WHITE_BALANCE_STOPS[0];
	let right = WHITE_BALANCE_STOPS[WHITE_BALANCE_STOPS.length - 1];
	for (let i = 0; i < WHITE_BALANCE_STOPS.length - 1; i++) {
		if (t >= WHITE_BALANCE_STOPS[i].t && t <= WHITE_BALANCE_STOPS[i + 1].t) {
			left = WHITE_BALANCE_STOPS[i];
			right = WHITE_BALANCE_STOPS[i + 1];
			break;
		}
	}

	const span = right.t - left.t || 1;
	const localT = (t - left.t) / span;
	const mix = (a: number, b: number) => Math.round(a + (b - a) * localT);
	const r = mix(left.rgb[0], right.rgb[0]);
	const g = mix(left.rgb[1], right.rgb[1]);
	const b = mix(left.rgb[2], right.rgb[2]);
	return `rgb(${r}, ${g}, ${b})`;
};

export const rgbTupleToCss = (rgb: number[] | undefined | null): string | null => {
	if (!Array.isArray(rgb) || rgb.length < 3) return null;
	const [r, g, b] = rgb;
	if (![r, g, b].every((c) => typeof c === 'number' && Number.isFinite(c))) return null;
	return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
};

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

export const scaleGlowSizesByBrightness = (
	surfaceGlow: SurfaceGlowConfig,
	brightnessPercent: number,
): SurfaceGlowConfig => {
	// Ambient / light brightness is 0–100. Map to a clear radius range so low
	// strength stays compact and full strength fills more of the card.
	const factor = clamp01(brightnessPercent / 100);
	const minFactor = 0.28;
	const scale = minFactor + (1 - minFactor) * factor;
	const active = surfaceGlow.active ?? {};
	const baseInterior = active.interiorSize ?? 150;
	const baseEdge = active.edgeSize ?? 190;
	return {
		...surfaceGlow,
		active: {
			...active,
			interiorSize: Math.round(baseInterior * scale),
			edgeSize: Math.round(baseEdge * scale),
			interiorStrength: (active.interiorStrength ?? 0.86) * (0.45 + 0.55 * factor),
			edgeStrength: (active.edgeStrength ?? 0.8) * (0.45 + 0.55 * factor),
		},
	};
};

/**
 * Cury ambient colour from settings (`cury:N.ambient.color`).
 * Caller must confirm the device is Cury (pack type entry) before relying on this.
 */
export const resolveCuryAmbientLightColor = (
	device: Device | undefined | null,
	status: any,
	settings: any,
): { color: string; active: boolean; brightness: number } | null => {
	if (!device?.type || !settings) return null;

	const channel = typeof device.channel === 'number' ? device.channel : 0;
	const key = `cury:${channel}`;
	const ambient = settings?.[key]?.ambient ?? settings?.['cury:0']?.ambient;
	if (!ambient) return null;

	const color = rgbTupleToCss(ambient.color);
	if (!color) return null;

	const statusBlock = status?.[key] ?? status?.['cury:0'];
	const active =
		ambient.enable === true || !!statusBlock?.output || !!statusBlock?.on || !!statusBlock?.ison;

	const brightness =
		typeof ambient.brightness === 'number' && Number.isFinite(ambient.brightness)
			? Math.max(0, Math.min(100, ambient.brightness))
			: typeof statusBlock?.ambient?.brightness === 'number'
				? Math.max(0, Math.min(100, statusBlock.ambient.brightness))
				: 100;

	return { color, active, brightness };
};

const hasLightStatusShape = (status: any): boolean => {
	if (!status) return false;
	if (Array.isArray(status.lights)) return true;
	return Object.keys(status).some((key) => /^(light|rgb|rgbw|rgbcct|cct):/.test(key));
};

/**
 * Resolve colour + brightness for light devices (RGB / CCT / white).
 * `allowLightStatus` should be true when the pack face is already light-color / device.
 */
export const resolveDashLightSurfaceColor = (
	device: Device | undefined | null,
	status: any,
	_settings: any,
	options?: { allowLightStatus?: boolean },
): { color: string; brightness: number; mode: 'color' | 'white' } | null => {
	if (!device || !status) return null;
	if (!options?.allowLightStatus && !hasLightStatusShape(status)) return null;

	const channel = typeof device.channel === 'number' ? device.channel : 0;

	const candidates = [
		status[`rgbw:${channel}`],
		status[`rgb:${channel}`],
		status[`rgbcct:${channel}`],
		status[`cct:${channel}`],
		status[`light:${channel}`],
		status['rgbw:0'],
		status['rgb:0'],
		status['rgbcct:0'],
		status['light:0'],
	].filter(Boolean);

	for (const block of candidates) {
		const brightness =
			typeof block.brightness === 'number'
				? block.brightness
				: typeof block.gain === 'number'
					? block.gain
					: 100;

		if (Array.isArray(block.rgb) && block.rgb.length >= 3) {
			const color = rgbTupleToCss(block.rgb);
			if (color) return { color, brightness, mode: 'color' };
		}

		const ct =
			typeof block.ct === 'number'
				? block.ct
				: typeof block.temp === 'number'
					? block.temp
					: typeof block.color_temp === 'number'
						? block.color_temp
						: null;
		if (ct != null) {
			return { color: whiteBalanceKelvinToCssRgb(ct), brightness, mode: 'white' };
		}
	}

	const gen1 = status?.lights?.[channel];
	if (gen1) {
		const brightness =
			typeof gen1.brightness === 'number'
				? gen1.brightness
				: typeof gen1.gain === 'number'
					? gen1.gain
					: 100;
		if (
			typeof gen1.red === 'number' &&
			typeof gen1.green === 'number' &&
			typeof gen1.blue === 'number'
		) {
			return {
				color: `rgb(${gen1.red}, ${gen1.green}, ${gen1.blue})`,
				brightness,
				mode: 'color',
			};
		}
		const ct = typeof gen1.temp === 'number' ? gen1.temp : gen1.ct;
		if (typeof ct === 'number') {
			return { color: whiteBalanceKelvinToCssRgb(ct), brightness, mode: 'white' };
		}
	}

	return null;
};

/** Read instantaneous power in watts from common Gen1 / Gen2+ shapes. */
export const getDashDevicePowerWatts = (
	device: Device | undefined | null,
	status: any,
): number | null => {
	if (!device || !status) return null;
	const channel = typeof device.channel === 'number' ? device.channel : 0;

	const gen2Keys = [
		`switch:${channel}`,
		`light:${channel}`,
		`cover:${channel}`,
		'switch:0',
		'light:0',
	];
	for (const key of gen2Keys) {
		const apower = status[key]?.apower;
		if (typeof apower === 'number' && Number.isFinite(apower)) return Math.max(0, apower);
	}

	const meter = status.meters?.[channel] ?? status.meters?.[0];
	if (typeof meter?.power === 'number' && Number.isFinite(meter.power)) {
		return Math.max(0, meter.power);
	}

	const roller = status.rollers?.[0];
	if (typeof roller?.power === 'number' && Number.isFinite(roller.power)) {
		return Math.max(0, roller.power);
	}

	return null;
};

/**
 * Relay glow for the glow-all pack: blue (non-PM) or green (PM).
 * PM brightness scales with watts (0–~2000 W → 28–100% radius factor).
 */
export const resolveRelayPowerGlow = (
	device: Device | undefined | null,
	status: any,
	isPm: boolean,
): { accentColor: string; brightness: number; isPm: boolean } => {
	if (!isPm) {
		return { accentColor: '--color-primary', brightness: 72, isPm: false };
	}

	const watts = getDashDevicePowerWatts(device, status);
	const maxWatts = 2000;
	const brightness =
		watts == null ? 55 : Math.round(28 + 72 * Math.min(1, Math.max(0, watts / maxWatts)));

	return { accentColor: '--color-success', brightness, isPm: true };
};
